"use client";

import React from "react";

export default function PrivacyPolicyPage() {
  return (
    <main className="min-h-screen bg-white">
      {/* Header section */}
      <div className="bg-[#fcfaf7] pt-40 pb-24 px-6 border-b border-[#ede8e0]">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="font-['DM_Mono'] text-[10px] uppercase tracking-[0.2em] text-[#b8955a] mb-4">
            Legal Information
          </p>
          <h1 className="text-4xl md:text-5xl font-serif italic text-[#121826]">
            Privacy Policy
          </h1>
          <div className="w-12 h-[1px] bg-[#b8955a] mx-auto mt-8"></div>
        </div>
      </div>

      {/* Content section */}
      <section className="max-w-[800px] mx-auto py-16 px-6 md:py-24">
        <div className="space-y-16 text-[#444] leading-[1.8]">
          
          <div className="border-l border-[#ede8e0] pl-8">
            <h2 className="text-sm font-['DM_Mono'] uppercase tracking-widest text-[#b8955a] mb-4">
              Overview
            </h2>
            <p className="text-lg text-[#121826] font-light italic leading-relaxed">
              At Modish Kreations, we value your privacy and are committed to protecting your personal information. 
              This policy outlines how we handle your data with the same care we give to our bespoke designs.
            </p>
            <p className="text-xs font-['DM_Mono'] mt-4 text-gray-400">
              Effective Date: May 2026
            </p>
          </div>

          <div className="grid gap-12">
           {/* Section 1 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">1. Information We Collect</h3>
                <p className="text-[15px] font-light">We may collect the following types of information:</p>
                <div className="grid md:grid-cols-2 gap-8 pt-2">
                  <div className="bg-[#faf8f5] p-6 rounded-sm">
                    <h4 className="font-['DM_Mono'] text-[11px] uppercase tracking-wider text-[#b8955a] mb-3">
                      a) Personal Information
                    </h4>
                    <ul className="text-sm space-y-2 font-light">
                      <li>• Name</li>
                      <li>• Email address</li>
                      <li>• Phone number</li>
                      <li>• Shipping & Billing Address</li>
                      <li>• Payment details (processed securely via third-party gateways)</li>
                    </ul>
                  </div>
                  <div className="bg-[#faf8f5] p-6 rounded-sm">
                    <h4 className="font-['DM_Mono'] text-[11px] uppercase tracking-wider text-[#b8955a] mb-3">
                      b) Non-Personal Information
                    </h4>
                    <ul className="text-sm space-y-2 font-light">
                      <li>• Browser type and version</li>
                      <li>• IP address</li>
                      <li>• Device information</li>
                      <li>• Pages visited and time spent on the website</li>
                    </ul>
                  </div>
                </div>
              </section>

              {/* Section 2 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">2. How We Use Your Information</h3>
                <p className="text-[15px] font-light">We use the collected information to:</p>
                <ul className="text-sm space-y-2 font-light pl-1">
                  <li>• Process and fulfill your orders</li>
                  <li>• Communicate with you regarding your purchases</li>
                  <li>• Improve our website and services</li>
                  <li>• Send promotional offers, newsletters, and updates (only if opted-in)</li>
                  <li>• Prevent fraudulent transactions and ensure security</li>
                </ul>
              </section>

              {/* Section 3 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">3. Sharing of Information</h3>
                <p className="text-[15px] font-light">
                  We do not sell or rent your personal data. However, we may share your information with:
                </p>
                <ul className="text-sm space-y-2 font-light pl-1">
                  <li>• Payment gateway providers for secure transactions</li>
                  <li>• Shipping and delivery partners</li>
                  <li>• Legal authorities if required by law</li>
                </ul>
              </section>

              {/* Section 4 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">4. Cookies & Tracking Technologies</h3>
                <p className="text-[15px] font-light">Our website uses cookies to:</p>
                <ul className="text-sm space-y-2 font-light pl-1">
                  <li>• Enhance user experience</li>
                  <li>• Analyze website traffic</li>
                  <li>• Remember your preferences</li>
                </ul>
                <p className="text-[15px] font-light">
                  You can disable cookies through your browser settings, though some features may not function properly.
                </p>
              </section>

              {/* Section 5 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">5. Data Security</h3>
                <p className="text-[15px] font-light">
                  We implement appropriate technical and organizational measures to protect your personal data from
                  unauthorized access, misuse, or disclosure. However, no online platform can guarantee 100% security.
                </p>
              </section>

              {/* Section 6 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">6. Your Rights</h3>
                <p className="text-[15px] font-light">You have the right to:</p>
                <ul className="text-sm space-y-2 font-light pl-1">
                  <li>• Access your personal data</li>
                  <li>• Request correction or deletion</li>
                  <li>• Opt-out of marketing communications</li>
                </ul>
                <p className="text-[15px] font-light">
                  To exercise these rights, contact us at the details below.
                </p>
              </section>

              {/* Section 7 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">7. Third-Party Links</h3>
                <p className="text-[15px] font-light">
                  Our website may contain links to third-party websites. We are not responsible for their privacy
                  practices and encourage you to read their policies.
                </p>
              </section>

              {/* Section 8 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">8. Changes to This Privacy Policy</h3>
                <p className="text-[15px] font-light">
                  Modish Kreations reserves the right to update this policy at any time. Changes will be posted on
                  this page with an updated effective date.
                </p>
              </section>

              {/* Section 9 */}
              <section className="space-y-4">
                <h3 className="text-xl font-serif text-[#121826] italic">9. Contact Us</h3>
                <p className="text-[15px] font-light">
                  If you have any questions about this Privacy Policy, you can contact us:
                </p>
                <div className="bg-[#faf8f5] p-6 rounded-sm text-sm font-light space-y-1">
                  <p className="font-medium text-[#121826]">Modish Kreations</p>
                  <p>Noida, Uttar Pradesh, India</p>
                </div>
              </section>
            <hr className="border-[#ede8e0] my-8" />
          </div>
        </div>
      </section>
      
    </main>
  );
}